import TransactionsType from "enums/TransactionsType";
import { useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { LocalImageAsset } from "./useAttachmentAndAssetHandling";
import {
  generateMileageExpenseValidationSchema,
  generateCashExpenseValidationSchema,
  expenseTypeValidationSchema,
} from "validations/createExpense";
import { toast } from "react-toastify";
import {
  APPROVE_PERSONAL_REIMBURSEMENT,
  CANCEL_PERSONAL_REIMBURSEMENT,
  GET_TRANSACTION,
  REJECT_PERSONAL_REIMBURSEMENT,
  REMOVE_RECEIPT_ASSET,
  REQUEST_PERSONAL_REIMBURSEMENT,
} from "graphql/transaction";
import { useMutation, useQuery } from "@apollo/client";
import httpClient from "utils/httpClient";
import { useAuth } from "@frontegg/react";
import { GET_MILEAGE_REIMBURSEMENT } from "graphql/company";
import { useTranslation } from "react-i18next";
import useCustomFieldRenderer from "./useCustomFieldRenderer";
import CustomFieldParentType from "enums/CustomFieldParentType";
import { isEqual } from "lodash";
import {
  formatExpenseForFormik,
  FormikCreateExpense,
  getExpenseInitialValuesByType,
} from "utils/reimbursements/reimbursementExpenseFormat";

interface UseCreateExpenseProps {
  expenseId?: string;
  expenseType?: TransactionsType | null;
  /**
   * Suppress the hook's per-step success/error toasts (expense created,
   * receipts uploaded, upload failed) so a caller can show a single
   * consolidated toast based on the `submitExpense()` result instead. Defaults
   * to `false` to keep the admin flow's existing behaviour.
   */
  suppressSubmitToasts?: boolean;
}

/** Outcome of the receipt-upload step within a submit. */
type ReceiptUploadOutcome = "none" | "uploaded" | "failed";

/** Structured result of `submitExpense`, for callers driving their own toasts. */
export interface SubmitExpenseResult {
  submitted: boolean;
  receipts: ReceiptUploadOutcome;
  errorMessage?: string;
}

const useCreateExpense = ({
  expenseId,
  expenseType,
  suppressSubmitToasts = false,
}: UseCreateExpenseProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [isLoadingReceiptUpload, setIsLoadingReceiptUpload] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(
    undefined,
  );

  const {
    customFieldSettings,
    customFieldsInitialValues,
    transformCustomFieldsToArray,
    transformCustomFieldsToRecord,
    isAnyLoading: loadingCustomFields,
  } = useCustomFieldRenderer({
    parentType: CustomFieldParentType.REIMBURSEMENT,
    inheritFrom:
      !expenseId && selectedUserId
        ? {
            type: CustomFieldParentType.USER,
            id: selectedUserId,
          }
        : undefined,
  });

  const { data: transactionData, loading: loadingTransactions } = useQuery(
    GET_TRANSACTION,
    {
      variables: {
        id: expenseId,
      },
      fetchPolicy: "cache-and-network",
      skip: !expenseId,
    },
  );

  const dynamicExpenseType = useMemo(() => {
    return transactionData?.transaction?.type ?? expenseType ?? null;
  }, [transactionData?.transaction?.type, expenseType]);

  const { data: mileageData } = useQuery(GET_MILEAGE_REIMBURSEMENT, {
    fetchPolicy: "cache-and-network",
    skip: dynamicExpenseType !== TransactionsType.MILEAGE,
  });

  // ========== VALIDATION SCHEMA ==========
  const validationSchema = useMemo(() => {
    switch (dynamicExpenseType) {
      case TransactionsType.CASH:
        return generateCashExpenseValidationSchema(customFieldSettings);
      case TransactionsType.MILEAGE:
        return generateMileageExpenseValidationSchema(customFieldSettings);
      default:
        return expenseTypeValidationSchema;
    }
  }, [dynamicExpenseType, customFieldSettings]);

  // ========== INITIAL VALUES ==========
  const initialValues = useMemo(() => {
    const rate = mileageData?.getMileageReimbursement?.rate || 0;
    return getExpenseInitialValuesByType(dynamicExpenseType, rate);
  }, [dynamicExpenseType, mileageData?.getMileageReimbursement?.rate]);

  // Captures the create-expense error message so a caller suppressing toasts
  // can surface it in its own consolidated toast.
  const submitErrorRef = useRef<string | undefined>(undefined);

  // ========== MUTATIONS ==========
  const [requestPersonalReimbursement, { loading }] = useMutation(
    REQUEST_PERSONAL_REIMBURSEMENT,
    {
      onCompleted: () => {
        if (!suppressSubmitToasts) {
          toast.success(t("Expense submitted successfully!"));
        }
      },
      onError: (err) => {
        submitErrorRef.current = err.message;
        if (!suppressSubmitToasts) {
          toast.error(err.message || t("Failed to submit expense"));
        }
      },
    },
  );

  const [approvePersonalReimbursement, { loading: approveLoading }] =
    useMutation(APPROVE_PERSONAL_REIMBURSEMENT);
  const [rejectPersonalReimbursement, { loading: rejectLoading }] = useMutation(
    REJECT_PERSONAL_REIMBURSEMENT,
  );
  const [cancelPersonalReimbursement, { loading: cancelLoading }] = useMutation(
    CANCEL_PERSONAL_REIMBURSEMENT,
  );

  const [removeReceiptAsset] = useMutation(REMOVE_RECEIPT_ASSET);

  // ========== HELPER: ASSETS ==========

  function transformAssetsToAttachments(assets?: any[]): LocalImageAsset[] {
    if (!assets) return [];
    return assets.map((asset) => ({
      assetUrl: asset.assetUrl,
      extension: asset.extension,
      id: asset.id,
      isExisting: true,
    }));
  }

  //todo when we move transaction receipt uplaod to useAttachmentAndAssetHandling, we can remove this
  const uploadReceipts = async (
    attachments: LocalImageAsset[],
    transactionId: string,
  ): Promise<ReceiptUploadOutcome> => {
    const newAttachments = attachments.filter(
      (attachment) => !attachment.isExisting,
    ); // dont need to upload existing attachments

    const initialAttachments = transformAssetsToAttachments(
      transactionData?.transaction?.receiptDetail?.assets,
    );

    const attachmentsToRemove = initialAttachments.filter(
      (initialAttachment) =>
        !attachments.some(
          (attachment) => attachment.id === initialAttachment.id,
        ),
    );
    const totalAttachmentsCount =
      initialAttachments.length -
      attachmentsToRemove.length +
      newAttachments.length;

    if (newAttachments.length === 0) {
      return "none";
    }

    if (totalAttachmentsCount > 5) {
      if (!suppressSubmitToasts) {
        toast.error(t("You can only upload 5 receipts total"));
      }
      return "failed";
    }

    // Remove attachments from the backend
    for (const attachment of attachmentsToRemove) {
      try {
        await removeReceiptAsset({
          variables: {
            receiptId: transactionData?.transaction?.receiptDetail?.id,
            assetId: attachment.id,
          },
        });
        toast.success(t("Attachment removed successfully"));
      } catch (err) {
        toast.error(t("Failed to remove attachment"));
      }
    }

    const formData = new FormData();
    // Append all selected files
    for (let i = 0; i < newAttachments.length; i++) {
      const file = newAttachments[i].file;
      if (!file) {
        console.error(`Attachment ${newAttachments[i].id} is missing a file`);
        if (!suppressSubmitToasts) {
          toast.error(t("Something went wrong"));
        }
        return "failed";
      }
      formData.append("receipts", file);
    }

    try {
      setIsLoadingReceiptUpload(true);
      await httpClient.post(
        `/transactions/${transactionId}/receipt`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${user?.accessToken}`,
            Prefer: "respond-async",
          },
        },
      );
      setIsLoadingReceiptUpload(false);
      if (!suppressSubmitToasts) {
        toast.success(t("ReceiptsQueuedToast"));
      }
      return "uploaded";
    } catch (err: any) {
      if (!suppressSubmitToasts) {
        toast.error(t("Failed to upload receipts"));
      }
      setIsLoadingReceiptUpload(false);
      return "failed";
    }
  };

  // Records whether the last submit attempt actually persisted, and how the
  // receipt upload fared. Formik's `submitForm()` resolves regardless of
  // validation/mutation outcome and the onSubmit below swallows errors, so
  // callers that need to react to the outcome (keep the drawer open, show a
  // single consolidated toast) read these via `submitExpense()`.
  const submitOutcomeRef = useRef(false);
  const receiptsOutcomeRef = useRef<ReceiptUploadOutcome>("none");

  // ========== FORMIK SETUP ==========
  const formikCreateExpense = useFormik<FormikCreateExpense>({
    initialValues: initialValues,
    validationSchema: validationSchema,
    validateOnMount: true,

    onSubmit: async (values) => {
      submitOutcomeRef.current = false;
      receiptsOutcomeRef.current = "none";
      submitErrorRef.current = undefined;
      let input = {};

      const commonInput = {
        payTo: values.payTo.id,
        amount: values.amount,
        type: values.expenseType,
        transactionDate:
          transactionData?.transaction?.transactionDate ?? new Date().getTime(),
        allocationCodeId: values.allocationCode.id,
        memo: values.description,
        customFields: transformCustomFieldsToArray(values.customFields),
      };

      switch (values.expenseType) {
        case TransactionsType.CASH:
          input = {
            ...commonInput,
            merchant: {
              id: "merchant" in values && values.merchant?.id,
              name: "merchant" in values && values.merchant?.name,
              url: "merchant" in values && values.merchant?.url,
            },
            lostReceipt: "lostReceipt" in values ? values.lostReceipt : false,
          };
          break;
        case TransactionsType.MILEAGE:
          input = {
            ...commonInput,
            tripTo: "tripTo" in values && values.tripTo,
            tripFrom: "tripFrom" in values && values.tripFrom,
            isRoundTrip: "isRoundTrip" in values && values.isRoundTrip,
            miles: "distance" in values && values.distance,
            polyline: "polyline" in values && values.polyline,
          };
          break;
        default:
          toast.error(t("Invalid expense type"));
          return;
      }

      try {
        const variables: { input: typeof input; expenseId?: string } = {
          input,
        };

        if (values.expenseId) {
          variables.expenseId = values.expenseId;
        }

        const { data } = await requestPersonalReimbursement({
          variables,
        });

        // With onError set, Apollo resolves a failed mutation instead of throwing.
        const transactionId = data?.requestPersonalReimbursement?.id;
        if (!transactionId) return;

        if ("attachments" in values && values.attachments) {
          receiptsOutcomeRef.current = await uploadReceipts(
            values.attachments,
            transactionId,
          );
        }
        submitOutcomeRef.current = true;
      } catch (error) {
        if (!suppressSubmitToasts) {
          toast.error(submitErrorRef.current || t("Failed to submit expense"));
        }
      }
    },
  });

  // ========== POPULATE FORM WHEN EDITING ==========
  useEffect(() => {
    if (transactionData?.transaction && expenseId) {
      // ========== EDITING EXISTING EXPENSE ==========
      const formikValues = formatExpenseForFormik(
        transactionData.transaction,
        transformCustomFieldsToRecord,
        customFieldSettings,
        mileageData?.getMileageReimbursement?.rate,
      );

      const hasAttachments =
        Array.isArray(transactionData.transaction.receiptDetail?.assets) &&
        transactionData.transaction.receiptDetail.assets.length > 0;

      if (hasAttachments) {
        formikValues.lostReceipt = false;
      } else {
        formikValues.lostReceipt = true;
      }

      formikCreateExpense.resetForm({ values: formikValues });
    } else if (!expenseId && customFieldsInitialValues) {
      // ========== CREATING NEW EXPENSE ==========
      const currentCustomFields = formikCreateExpense.values.customFields || {};

      if (!isEqual(currentCustomFields, customFieldsInitialValues)) {
        // The payee's defaults are a starting point, so an untouched form must not read as changed.
        if (formikCreateExpense.dirty) {
          formikCreateExpense.setFieldValue(
            "customFields",
            customFieldsInitialValues,
          );
        } else {
          formikCreateExpense.resetForm({
            values: {
              ...formikCreateExpense.values,
              customFields: customFieldsInitialValues,
            },
          });
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    expenseId,
    transactionData?.transaction,
    customFieldSettings,
    customFieldsInitialValues,
    mileageData?.getMileageReimbursement?.rate,
  ]);

  useEffect(() => {
    const userId = formikCreateExpense.values?.payTo?.id;
    if (userId !== selectedUserId) {
      setSelectedUserId(userId);
    }
  }, [formikCreateExpense.values?.payTo?.id, selectedUserId]);

  useEffect(() => {
    if (
      dynamicExpenseType !== TransactionsType.MILEAGE ||
      !mileageData?.getMileageReimbursement?.rate
    )
      return;

    formikCreateExpense.setFieldValue(
      "rate",
      mileageData?.getMileageReimbursement?.rate,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mileageData?.getMileageReimbursement?.rate]);

  const resetFormValues = (type: TransactionsType | null) => {
    const rate = mileageData?.getMileageReimbursement?.rate || 0;
    const newValues = getExpenseInitialValuesByType(type, rate);

    // Preserve the payee and custom fields when switching types
    if (type) {
      newValues.payTo = formikCreateExpense.values.payTo;
      newValues.customFields = formikCreateExpense.values.customFields;
      formikCreateExpense.setValues(newValues);
    } else {
      formikCreateExpense.resetForm();
    }
  };

  /**
   * Submit and report the outcome. Resets the outcome flags, runs Formik
   * submission (validation + mutation + receipt upload), then returns whether
   * the expense persisted and how the receipt upload fared — so callers can
   * keep the drawer open on failure and show a single consolidated toast
   * covering both the expense and its receipts.
   */
  const submitExpense = async (): Promise<SubmitExpenseResult> => {
    submitOutcomeRef.current = false;
    receiptsOutcomeRef.current = "none";
    submitErrorRef.current = undefined;
    await formikCreateExpense.submitForm();
    return {
      submitted: submitOutcomeRef.current,
      receipts: receiptsOutcomeRef.current,
      errorMessage: submitErrorRef.current,
    };
  };

  return {
    formikCreateExpense,
    submitExpense,
    customFieldSettings,
    customFieldsInitialValues,
    resetFormValues,
    loading: loading || isLoadingReceiptUpload || loadingTransactions,
    loadingCustomFields,
    approveLoading,
    rejectLoading,
    cancelLoading,
    approvePersonalReimbursement,
    rejectPersonalReimbursement,
    cancelPersonalReimbursement,
  };
};

export default useCreateExpense;
